import moment from "moment";
import momentTimezone from "moment-timezone";

/**
 * Default display format for dates rendered in the viewer's *browser-local*
 * timezone. Used when a date should feel "local" to the operator reading it
 * (e.g. the legacy `Date` column). Includes the `ZZ` offset so the timezone is
 * explicit.
 *
 * @type {string}
 */
const LOCAL_DATE_FORMAT = "DD/MM/YYYY hh:mm:ss A ZZ";

/**
 * UTC display format used whenever a date must match how it is *stored in the
 * database* (Mongo `timestamps` are UTC). Preferred for audit/operations views
 * so every operator — regardless of their machine timezone — sees the same
 * instant. Includes `A` (AM/PM) for human readability.
 *
 * @type {string}
 */
const UTC_DATE_FORMAT = "DD/MM/YYYY hh:mm:ss A";

/**
 * Format a date for display in the viewer's **browser-local** timezone.
 *
 * Reusable, null-safe formatter. Returns `"-"` for empty/invalid input so it
 * can be dropped straight into a table cell formatter.
 *
 * @param {string|Date|number|import("moment").Moment} date - The value to format.
 *   Accepts an ISO string, a `Date`, a unix ms number, or a Moment instance.
 * @param {string} [format=LOCAL_DATE_FORMAT] - Optional Moment format override.
 * @returns {string} The formatted date, or `"-"` when the input is empty/invalid.
 *
 * @example
 * // ISO string -> local time with offset
 * formatDate("2024-01-15T10:30:00.000Z");
 * // => "15/01/2024 12:30:00 PM +02:00"  (viewer in UTC+2)
 *
 * @example
 * // Used as a react-bootstrap-table column formatter
 * {
 *   dataField: "createdAt",
 *   text: t("Creation Date"),
 *   formatter: (row) => formatDate(row.createdAt),
 * }
 */
export default function formatDate(date, format = LOCAL_DATE_FORMAT) {
  if (!date) return "-";

  if (moment.isMoment(date)) {
    return date.format(format);
  }

  if (typeof date === "number" || date instanceof Date) {
    const parsedDate = moment(date);
    return parsedDate.isValid() ? parsedDate.format(format) : "-";
  }

  if (typeof date === "string") {
    const parsedDate = moment(
      date,
      [
        moment.ISO_8601,
        "DD/MM/YYYY hh:mm:ss A ZZ",
        "DD/MM/YYYY hh:mm:ss A",
        "DD/MM/YYYY",
      ],
      true
    );

    if (parsedDate.isValid()) {
      return parsedDate.format(format);
    }

    return date;
  }

  const parsedDate = moment(date);
  return parsedDate.isValid() ? parsedDate.format(format) : "-";
}

/**
 * Format a date for display in **UTC**, matching how the value is stored in the
 * database. This is the canonical formatter for audit/operations columns
 * (Creation Date, Approval/Reject Date, etc.) so the instant is identical for
 * every operator, independent of their machine timezone.
 *
 * Reusable, null-safe formatter. Returns `"-"` for empty/invalid input.
 *
 * @param {string|Date|number|import("moment").Moment} date - The value to format.
 * @param {string} [format=UTC_DATE_FORMAT] - Optional Moment format override.
 * @returns {string} The UTC-formatted date, or `"-"` when input is empty/invalid.
 *
 * @example
 * // ISO string -> UTC, 12-hour clock with AM/PM (no offset, it is UTC)
 * formatDateUtc("2024-01-15T10:30:00.000Z");
 * // => "15/01/2024 10:30:00 AM"
 *
 * @example
 * // End-to-end usage in a Client Profile transaction table.
 * // Both columns below render in UTC so Creation vs Approval delta is exact:
 * const columns = [
 *   {
 *     dataField: "createdAt",
 *     text: t("Creation Date"),
 *     // request submitted (always shown, even while PENDING)
 *     formatter: (row) => formatDateUtc(row.createdAt),
 *   },
 *   {
 *     dataField: "updatedAt",
 *     text: t("Approval/Reject Date"),
 *     // settled moment; dash while the row is still PENDING
 *     formatter: (row) =>
 *       row?.status === "PENDING" ? "-" : formatDateUtc(row.updatedAt),
 *   },
 * ];
 */
export function formatDateUtc(date, format = UTC_DATE_FORMAT) {
  if (!date) return "-";

  const parsedDate = typeof date === "string"
    ? moment.utc(
      date,
      [
        moment.ISO_8601,
        "DD/MM/YYYY hh:mm:ss A ZZ",
        "DD/MM/YYYY hh:mm:ss A",
        "DD/MM/YYYY",
      ],
      true
    )
    : moment.utc(date);

  return parsedDate.isValid() ? parsedDate.format(format) : "-";
}

/**
 * Broker / MT5 server time (UTC+3). MUST mirror backend `REPORT_TIMEZONE`
 * (`Etc/GMT-3`) in backend-api/src/common/lib/reportTimezone.js. Use this for any
 * transactional date that should match the UTC+3 day-boundary filter and the
 * broker-time report downloads, independent of the operator's machine timezone.
 * Accepts a Date, an ISO string (with or without offset), an epoch (ms), or a
 * `DD/MM/YYYY hh:mm:ss A` string.
 *
 * @example
 * // Raw Date from the API -> UTC+3 wall clock
 * formatDateBroker(row.createdAt); // => "15/07/2026 02:00:00 AM"
 */
export const REPORT_TZ = "Etc/GMT-3";
export function formatDateBroker(date, format = UTC_DATE_FORMAT) {
  if (!date) return "-";
  const parsed = momentTimezone.tz(date, REPORT_TZ);
  return parsed.isValid() ? parsed.format(format) : "-";
}
